/**
 * Cliente HTTP del saldo de créditos, compartido por las apps de la familia
 * (`GET /api/me/credits`). El endpoint devuelve el shape plano (sin envelope
 * `{ success, data }`) — confirmado contra `apps/backend/src/routes/billing.ts`.
 *
 * Los montos viajan como string para preservar precisión BigInt (1 USD =
 * 10^8 nano). El display al usuario es en **orbs** (1 orb = $0,001 = 100_000
 * nano): la conversión vive en `@orborix/shared-types` (`nanoToOrbs` /
 * `formatOrbs`), única fuente de la tasa. El `balanceUsd` que devuelve el
 * backend NO se muestra (es interno; el ledger sigue en nano-USD).
 */

import { apiFetch } from '@orborix/auth-client';

/** Error con código crudo del backend; el caller decide el copy user-facing. */
export class BillingApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(message: string, status: number, code: string) {
    super(message);
    this.name = 'BillingApiError';
    this.status = status;
    this.code = code;
  }
}

export interface CreditsBalance {
  /** Saldo en nano-USD (1e8 nano = $1). String BigInt-safe. */
  balanceNano: string;
  /** Saldo USD pre-formateado por el backend con 2 decimales. */
  balanceUsd: string;
  /** 'credits' = saldo central; 'byok' = no aplica saldo (no se muestra pill). */
  mode: 'byok' | 'credits';
  /** ISO 8601. */
  updatedAt: string;
}

export async function getCredits(): Promise<CreditsBalance> {
  let res: Response;
  try {
    res = await apiFetch('/api/me/credits');
  } catch {
    throw new BillingApiError('No pudimos conectar con el servidor.', 0, 'network_error');
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new BillingApiError(body.error ?? `HTTP ${res.status}`, res.status, body.error ?? 'unknown');
  }
  return (await res.json()) as CreditsBalance;
}
